import { ImageContainer } from 'app/ui-components/PlayImage/PlayImage.styles';
import { BlurhashCanvas } from 'react-blurhash';
import styled, { css } from 'styled-components';
import { device, deviceGte } from 'app/ui-components/breakpoints';
import {
    fontWeightBold,
    fontWeightLight,
    iconPlay,
    iconPrevious,
    playGrey10,
    playGrey15,
    regularBlack,
    regularWhite,
    iconClock,
    fontWeightRegular,
} from 'app/ui-components/design-tokens';
import PlayBadge from 'app/ui-components/PlayBadge/PlayBadge';
import { Heading1, Heading5, Heading5Typography } from 'app/ui-components/typography/Heading.styles';
import { playFontIcon, playFontSize } from 'app/ui-components/typography/helpers';
import PlayText from 'app/ui-components/typography/PlayText/PlayText';
import { VisuallyHidden } from 'app/ui-components/utils.styles';

export const StyledTopArea = styled.div`
    overflow: hidden;

    @media ${device.GteLarge} {
        display: flex;
        flex: 1 auto;
        position: relative;
    }
`;

export const StyledTopAreaPlaceholder = styled(StyledTopArea)``;

export const StyledTopAreaLeft = styled.div`
    @media ${device.GteLarge} {
        width: calc(((100% / 5) * 3) - 4px);
    }

    @media ${device.FullScreen} {
        width: 50%;
    }

    ${ImageContainer} {
        background-color: rgba(0, 0, 0, 0.3);
    }
`;

export const StyledTopAreaLeftPlaceholder = styled(StyledTopAreaLeft)`
    padding-top: 56.25%;
    background-color: ${playGrey15};

    @media ${device.GteLarge} {
        padding-top: 33.547%;
    }

    @media ${device.FullScreen} {
        padding-top: 28.125%;
    }
`;

export const StyledTopAreaRight = styled.div`
    @media ${device.GteLarge} {
        position: relative;
        display: inline-flex;
        width: 40%;
    }
`;

export const StyledTopAreaRightPlaceholder = styled(StyledTopAreaRight)`
    background-color: ${playGrey10};
    align-self: stretch;
    height: 38px;
    margin-top: 15px;
    width: 50%;

    @media ${device.GteLarge} {
        margin-top: 0;
        height: auto;
    }
`;

export const StyledTopAreaMeta = styled.div`
    margin-top: 15px;

    @media ${device.GteLarge} {
        align-self: center;
        margin: 0 30px;
    }
`;

export const StyledTopAreaHeading = styled(Heading1)`
    font-size: 3rem;
    line-height: 1;
    margin-bottom: 0.3em;
`;

export const Byline = styled(PlayText)`
    text-transform: uppercase;
    letter-spacing: 0.4px;
`;

export const StyledTopAreaPlayButtonArea = styled.div`
    display: flex;
    flex-direction: column;
    margin: 20px 0 0;

    @media ${deviceGte(375)} {
        ${({ playFromPosition }) =>
            !playFromPosition &&
            css`
                margin: 20px 0 0;
                flex-direction: row;
            `}
    }

    @media ${device.GteXsmall} {
        ${({ playFromPosition }) =>
            playFromPosition &&
            css`
                margin: 20px 0 15px;
                flex-direction: row;
            `}
    }

    @media ${device.GteMedium} {
        margin-top: 15px;
    }

    @media ${device.GteLarge} {
        flex-direction: ${({ playFromPosition }) => (playFromPosition ? 'column' : 'row')};
        margin-top: 10px;
    }

    @media ${device.GteXLarge} {
        margin-top: 30px;
        flex-direction: row;
    }

    ${(props) =>
        props.onlyAvailableInSweden &&
        css`
            .no-sweden & {
                ${VisuallyHidden};
            }
        `}
`;

export const StyledTopAreaPlayButton = styled.a`
    ${playFontSize(1.3)}
    color: ${regularBlack};
    width: min-content;
    text-decoration: none;
    font-weight: 700;
    text-transform: uppercase;
    height: 37px;
    border: 1px solid transparent;
    background-color: ${regularWhite};
    border-radius: 33px;
    display: flex;
    justify-content: left;
    align-items: center;
    cursor: pointer;
    transition: max-width 500ms ease-in-out, background-color 120ms ease-in-out;
    position: relative;
    overflow: hidden;

    &:hover,
    &:focus {
        background-color: rgba(255, 255, 255, 0.9);
    }

    &:focus {
        outline: none;
        border-color: ${regularBlack};
    }

    &:before {
        ${({ previousIcon }) => playFontIcon(2.3, previousIcon ? iconPrevious : iconPlay)}
        position: absolute;
        left: 10px;
    }
   
    @media ${device.GteLarge} {
        margin: ${({ playFromPosition }) => (playFromPosition ? ' 0 0 0 0' : '10px 10px 0 0')};  
    }

    @media ${device.GteXLarge} {
        margin: 0 0 0 0;  
    }

    ${(props) =>
        props.onlyAvailableInSweden &&
        css`
            .no-sweden & {
                ${VisuallyHidden};
            }
        `}

    ${(props) =>
        props.secondaryButton &&
        css`
            background-color: rgba(255, 255, 255, 0.1);
            color: ${regularWhite};
            margin: 10px 0 0 0;

            @media ${deviceGte(375)} {
                ${({ playFromPosition }) =>
                    !playFromPosition &&
                    css`
                        margin: 0 0 0 10px;
                    `}
            }

            @media ${device.GteXsmall} {
                ${({ playFromPosition }) =>
                    playFromPosition &&
                    css`
                        margin: 0 0 0 10px;
                    `}
            }

            @media ${device.GteLarge} {
                margin: 10px 0 0 0;
            }

            @media ${device.GteXLarge} {
                margin: 0 0 0 10px;
            }

            &:hover,
            &:focus {
                background-color: rgba(255, 255, 255, 0.15);
            }
        `}
`;

export const PlaySpan = styled.span`
    display: inline-block;

    @media ${device.GteXLarge} {
        display: none;
    }

    @media ${deviceGte(1420)} {
        display: inline-block;
    }
`;

export const NotAvailableInSweden = styled(PlayText)`
    display: none;
    ${playFontSize(1.7)}

    @media ${device.GteSmall} {
        ${playFontSize(2)}
        margin-top: 30px;
        margin-bottom: 0;
    }

    ${(props) =>
        props.onlyAvailableInSweden &&
        css`
            .no-sweden & {
                display: block;
            }
        `}
`;

export const HiddenByline = styled.p`
    ${VisuallyHidden};
`;

export const StyledTopAreaPlayButtonText = styled.span`
    margin-left: 40px;
    margin-right: 30px;
    white-space: nowrap;
`;

export const StyledTopAreaTitle = styled(Heading5)`
    font-weight: ${fontWeightLight};
    display: inline-flex;
    align-items: center;
`;

export const StyledBlurHashCanvas = styled(BlurhashCanvas)`
    display: none;

    @media ${device.GteLarge} {
        display: block;
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        z-index: -1;
        object-fit: cover;
        opacity: 0.4;
    }
`;

export const StyledTopAreaSubheading = styled.p`
    ${Heading5Typography};
    font-weight: normal;
    display: block;

    @media ${device.GteSmall} {
        ${playFontSize(1.8)}
    }

    @media ${device.GteLarge} {
        ${playFontSize(2.2)}
    }

    @media ${device.FullScreen} {
        ${playFontSize(2.6)}
    }

    @media ${device.GteMedium} {
        margin-top: 4px;
    }
`;

export const StyledTopAreaHighlightText = styled(PlayText)`
    margin-top: 10px;

    @media ${device.GteSmall} {
        /* stylelint-disable value-no-vendor-prefix */
        display: -webkit-box;
        /* stylelint-enable value-no-vendor-prefix */
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
    }
`;

export const StyledTopAreaBadge = styled(PlayBadge)`
    margin-bottom: 5px;

    @media ${device.GteLarge} {
        margin-bottom: 10px;
    }
`;

export const StyledTopAreaUpcomingLive = styled.p`
    color: ${regularWhite};
    font-weight: ${fontWeightBold};
    display: flex;
    align-items: center;
    ${playFontSize(1.8)}

    @media ${device.GteSmall} {
        ${playFontSize(2)}
    }

    @media ${device.GteLarge} {
        margin-top: 15px;
    }

    @media ${device.FullScreen} {
        margin-top: 0;
        ${playFontSize(2.4)}
    }

    &:before {
        ${playFontIcon(1, iconClock)}
        font-size: 1.3em;
        margin-right: 10px;
        font-weight: ${fontWeightRegular};
    }
`;
